// eslint-disable-next-line jira/restricted/react-component-props
import React, { type ComponentType, type ComponentProps, useCallback } from 'react';
import { AppSwitcher as AkAppSwitcher } from '@atlaskit/atlassian-navigation';
import type DrawerDI from '@atlaskit/drawer';
import PopupDI, { type TriggerProps } from '@atlaskit/popup';
import {
	ViewExperienceSuccessTracker as ViewExperienceSuccessTrackerDI,
	ViewExperienceTrackingProvider as ViewExperienceTrackingProviderDI,
	type TrackerWithoutCallbacksProps,
	type ViewExperienceTrackingProviderProps,
} from '@atlassian/jira-common-experience-tracking-viewing';
import { ErrorBoundary } from '@atlassian/jira-error-boundary';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { useTenantContext as useTenantContextDI } from '@atlassian/jira-tenant-context-controller';
import { SwitchToTooltipText, AtlassianSwitcherPrefetchTrigger } from '@atlassian/switcher';
import { AppSwitcherNudgeToolTip } from './app-switcher-nudge-tooltip';

import { StyledSwitcherContainer } from './styled';
import type { IntegrationPopupProps } from './types';

type Props = IntegrationPopupProps & {
	Popup?: ComponentType<
		JSX.LibraryManagedAttributes<typeof PopupDI, ComponentProps<typeof PopupDI>>
	>;
	Drawer?: ComponentType<
		JSX.LibraryManagedAttributes<typeof DrawerDI, ComponentProps<typeof DrawerDI>>
	>;
	AppSwitcher?: ComponentType<
		JSX.LibraryManagedAttributes<typeof AkAppSwitcher, ComponentProps<typeof AkAppSwitcher>>
	>;
	useTenantContext?: typeof useTenantContextDI;
	ViewExperienceTrackingProvider?: ComponentType<ViewExperienceTrackingProviderProps>;
	ViewExperienceSuccessTracker?: ComponentType<TrackerWithoutCallbacksProps>;
};

const JIRA_PRODUCT = 'jira';

export const IntegrationPopup = ({
	Popup = PopupDI,
	AppSwitcher = AkAppSwitcher,
	useTenantContext = useTenantContextDI,
	ViewExperienceTrackingProvider = ViewExperienceTrackingProviderDI,
	ViewExperienceSuccessTracker = ViewExperienceSuccessTrackerDI,
	...popupProps
}: Props) => {
	const [endUserActivateExpConfig] = UNSAFE_noExposureExp('end_user_activate_spotlights_v2');
	const { cloudId } = useTenantContext();
	const { isOpen, children, Drawer, icon, onOpen, ...akPopupProps } = popupProps;
	const renderSwitcher = useCallback(
		(triggerProps: TriggerProps) => (
			<AppSwitcher
				{...triggerProps}
				isSelected={popupProps.isOpen}
				onClick={() => {
					popupProps.isOpen ? popupProps.onClose() : popupProps.onOpen();
				}}
				tooltip={SwitchToTooltipText}
			/>
		),
		[AppSwitcher, popupProps],
	);

	return (
		<>
			<Popup
				{...akPopupProps}
				isOpen={popupProps.isOpen}
				placement="bottom-start"
				content={() => (
					<ViewExperienceTrackingProvider
						application={null}
						analyticsSource="atlassian-navigation"
						experience="viewAtlassianNavigationMenu"
						experienceId="atlassianSwitcher"
						edition={null}
					>
						<ViewExperienceSuccessTracker
							location="atlassian-navigation--secondary-actions-app-switcher--menu-popup"
							parentProviders={null}
							failureEventAttributes={null}
						>
							<StyledSwitcherContainer>{popupProps.children}</StyledSwitcherContainer>
						</ViewExperienceSuccessTracker>
					</ViewExperienceTrackingProvider>
				)}
				trigger={(triggerProps) => (
					<AtlassianSwitcherPrefetchTrigger cloudId={cloudId} product={JIRA_PRODUCT}>
						{endUserActivateExpConfig.get('cohort', 'not-enrolled') !== 'not-enrolled' ? (
							<ErrorBoundary
								id="app-switcher-nudge-tooltip"
								packageName="jira-atlassian-navigation-switcher-integration"
								render={() => renderSwitcher(triggerProps)}
							>
								<AppSwitcherNudgeToolTip cloudId={cloudId}>
									{renderSwitcher(triggerProps)}
								</AppSwitcherNudgeToolTip>
							</ErrorBoundary>
						) : (
							renderSwitcher(triggerProps)
						)}
					</AtlassianSwitcherPrefetchTrigger>
				)}
			/>
		</>
	);
};
